import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, BadgeIndianRupee, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { resolveImageUrl, formatRental } from "@/lib/api";

const GOA_CENTER = [15.56, 73.81];
const INDORE_CENTER = [22.7196, 75.8577];

const pinIcon = (gold) =>
  L.divIcon({
    className: "",
    html: `<div class="rc-map-pin ${gold ? "rc-map-pin-gold" : ""}"><span></span></div>`,
    iconSize: [30, 40],
    iconAnchor: [15, 40],
    popupAnchor: [0, -36],
  });

// One pin per project; individual pins for standalone listings. Spread identical coordinates.
const buildPins = (properties, lang) => {
  const groups = {};
  for (const p of properties) {
    if (p.lat == null || p.lng == null) continue;
    const key = p.project_slug || p.id;
    (groups[key] = groups[key] || []).push(p);
  }
  const seen = {};
  return Object.entries(groups).map(([key, units]) => {
    const first = units[0];
    const rentals = units.map((u) => u.assured_rental_monthly).filter(Boolean);
    const coordKey = `${first.lat},${first.lng}`;
    const n = (seen[coordKey] = (seen[coordKey] || 0) + 1) - 1;
    const angle = n * 1.2;
    const r = n ? 0.004 + n * 0.0015 : 0;
    return {
      key,
      position: [first.lat + r * Math.sin(angle), first.lng + r * Math.cos(angle)],
      name: first.project_name || (lang === "hi" && first.title_hi) || first.title_en,
      location: [first.locality, first.city].filter(Boolean).join(", "),
      image: resolveImageUrl(first.image_paths?.[0]),
      minRental: rentals.length ? Math.min(...rentals) : null,
      multiRental: new Set(rentals).size > 1,
      href: first.project_slug ? `/project/${first.project_slug}` : `/property/${first.id}`,
    };
  });
};

const JumpButtons = () => {
  const map = useMap();
  const btn =
    "px-3 py-1.5 rounded-full bg-[#0B2545] text-[#C9A84C] text-[11px] font-bold uppercase tracking-wider border border-[#C9A84C]/60 hover:bg-[#133763] transition-colors";
  return (
    <div className="absolute top-3 right-3 z-[1000] flex gap-2">
      <button data-testid="map-jump-goa" className={btn} onClick={() => map.flyTo(GOA_CENTER, 11)}>Goa</button>
      <button data-testid="map-jump-indore" className={btn} onClick={() => map.flyTo(INDORE_CENTER, 12)}>Indore</button>
    </div>
  );
};

const PinPopup = ({ pin }) => {
  const { t } = useLang();
  return (
    <div className="w-56" data-testid={`map-popup-${pin.key}`}>
      {pin.image && <img src={pin.image} alt={pin.name} className="w-full h-28 object-cover rounded-md" />}
      <div className="mt-2 font-serif text-base font-bold text-[#0B2545] leading-tight">{pin.name}</div>
      <div className="mt-1 flex items-center gap-1 text-xs text-[#1F2A44] font-semibold">
        <MapPin size={12} className="text-[#C9A84C]" /> {pin.location}
      </div>
      {pin.minRental && (
        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#C9A84C] text-[#0B2545] text-xs font-black">
          <BadgeIndianRupee size={13} />
          {pin.multiRental ? "From " : ""}
          {formatRental(pin.minRental)}{t.rental.per_month}
        </div>
      )}
      <Link
        to={pin.href}
        data-testid={`map-popup-link-${pin.key}`}
        className="mt-3 flex items-center justify-center gap-1 h-9 rounded-md bg-[#0B2545] !text-[#C9A84C] text-[11px] font-bold uppercase tracking-wider hover:bg-[#133763]"
      >
        View Listing <ArrowRight size={12} />
      </Link>
    </div>
  );
};

export const PropertyMap = ({ properties }) => {
  const { lang } = useLang();
  const [rentalOnly, setRentalOnly] = useState(false);
  const allPins = useMemo(() => buildPins(properties, lang), [properties, lang]);
  const pins = rentalOnly ? allPins.filter((p) => p.minRental) : allPins;
  return (
    <div>
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={() => setRentalOnly((v) => !v)}
        data-testid="map-filter-assured-rental"
        aria-pressed={rentalOnly}
        className={`inline-flex items-center gap-2 h-10 px-4 rounded-full border-2 text-xs font-bold uppercase tracking-wider transition-colors ${
          rentalOnly
            ? "bg-[#C9A84C] border-[#C9A84C] text-[#0B2545]"
            : "bg-white border-[#C9A84C] text-[#0B2545] hover:bg-[#FFFDF5]"
        }`}
      >
        <BadgeIndianRupee size={15} strokeWidth={2.5} /> Assured Rental Only
      </button>
      <span className="text-sm text-[#1F2A44] font-medium" data-testid="map-pin-count">
        {pins.length} {pins.length === 1 ? "location" : "locations"} on map
      </span>
    </div>
    <div
      data-testid="property-map"
      className="relative h-[70vh] min-h-[420px] rounded-2xl overflow-hidden border-2 border-[#0B2545] z-0"
    >
      <MapContainer center={GOA_CENTER} zoom={11} scrollWheelZoom className="h-full w-full">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {pins.map((pin) => (
          <Marker key={pin.key} position={pin.position} icon={pinIcon(!!pin.minRental)}>
            <Popup autoPanPadding={[24, 24]} maxWidth={240}>
              <PinPopup pin={pin} />
            </Popup>
          </Marker>
        ))}
        <JumpButtons />
      </MapContainer>
    </div>
    </div>
  );
};
